import {
    COLOUR_BACKUP_FAILED,
    COLOUR_BACKUP_POSSIBLE_GAP,
    COLOUR_BACKUP_RESTORE_WINDOW,
} from '../../component/Constants';
import { EChartsCoreOption } from '../../component/chart/echarts/echartsCore';
import { positionTooltip, TOOLTIP_BASE } from '../../component/chart/echarts/tooltipAdapter';
import { axisLabelFormat, formatAxisLabel, buildPlotLineSeries } from '../../component/chart/echarts/timeSeriesOption';
import ChartPlotLine from '../../types/ChartPlotLine';
import { formatBytes } from '../../helpers/utils';
import BackupHistoryItem from '../../types/backup/BackupHistoryItem';
import BackupSummary from '../../types/backup/BackupSummary';
import {
    BackupChartMetric,
    BACKUP_KIND_COLOURS,
    BACKUP_KIND_LABELS,
    BACKUP_KINDS,
    backupKind,
    backupTypeLabel,
    formatBackupDuration,
    formatBackupTime,
} from './backupUtils';
import { chainBreakLabel, isPossibleGap } from './backupChainUtils';

export interface BackupHistoryOptionArgs {
    items: BackupHistoryItem[];
    days: number;
    metric: BackupChartMetric;
    // For the point-in-time restore window of the current chain.
    row: BackupSummary;
    timezone: string;
    now: number;
    plotLines?: ChartPlotLine[];
}

const MILLIS_IN_DAY = 24 * 60 * 60 * 1000;
const BAR_WIDTH = 3;
const BAR_MIN_HEIGHT = 2;
// Breaks closer than this fraction of the time axis share a line, or their
// labels overprint.
const BREAK_MERGE_FRACTION = 0.015;

const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const point = (item: BackupHistoryItem, y: number) => ({
    value: [Date.parse(item.timeslice), y],
    item,
});

const byTime = (a: { value: number[] }, b: { value: number[] }) => a.value[0] - b.value[0];

export const formatBackupTooltip = (item: BackupHistoryItem): string => {
    const lines = [
        `<b>${escapeHtml(backupTypeLabel(item.backuptype))}${item.status === 'failed' ? ' failed' : ''}</b>`,
        formatBackupTime(item.timeslice),
    ];
    if (item.status === 'failed') {
        lines.push(escapeHtml(item.message || ''));
    } else {
        lines.push(`Duration: ${formatBackupDuration(item.duration)}`);
        lines.push(`Size: ${formatBytes(item.size)}`);
        lines.push(`Compressed size: ${formatBytes(item.compressedsize)}`);
    }
    if (item.servername) {
        lines.push(`Replica: ${escapeHtml(item.servername)}`);
    }
    return `<div class="chart-tooltip-new">${lines.join('<br/>')}</div>`;
};

// The times the chain broke, grouped where they are within gap of each
// group's first break, as that break and how many the group has. Measuring
// from the first break keeps a steady run of breaks from becoming one group.
export const groupChainBreaks = (times: number[], gap: number): { time: number; count: number }[] => {
    const groups: { time: number; count: number }[] = [];
    Array.from(new Set(times)).sort((a, b) => a - b).forEach(time => {
        const group = groups[groups.length - 1];
        if (group && time - group.time <= gap) {
            group.count++;
        } else {
            groups.push({ time, count: 1 });
        }
    });
    return groups;
};

// Each backup as a bar at its start time, as tall as its duration, size or
// compressed size, coloured by kind. Failures have none of these so are
// markers on the axis. A break in the log chain is a red line where the chain
// stops restoring, with breaks close together sharing one line, or a grey
// possible gap where an unmonitored replica may hold the missing logs. The
// current chain's point-in-time restore window is shaded.
export const buildBackupHistoryOption = (args: BackupHistoryOptionArgs): EChartsCoreOption => {
    const { items, metric, row, timezone, now } = args;
    const min = now - args.days * MILLIS_IN_DAY;
    const labelFormat = axisLabelFormat(now - min);
    const formatValue = (value?: number | null) =>
        metric === 'duration' ? formatBackupDuration(value) : formatBytes(value);

    const bars = BACKUP_KINDS.map(kind => ({
        type: 'bar',
        name: BACKUP_KIND_LABELS[kind],
        color: BACKUP_KIND_COLOURS[kind],
        barWidth: BAR_WIDTH,
        barMinHeight: BAR_MIN_HEIGHT,
        // Overlap rather than group side by side, as each backup is its own bar.
        barGap: '-100%',
        itemStyle: { borderWidth: 0 },
        data: items
            .filter(item => item.status === 'success' && backupKind(item.backuptype) === kind)
            .map(item => point(item, item[metric] ?? 0))
            .sort(byTime),
    }));

    // Breaks before the axis would draw nowhere, and take any grouped with
    // them along, so they are left out. Possible gaps and breaks are grouped
    // apart, as each line has one colour and label.
    const breaks = items.filter(item => item.chainbreak && item.chainbreakafter &&
        Date.parse(item.chainbreakafter) >= min);
    const breakLines = (possible: boolean) => {
        const colour = possible ? COLOUR_BACKUP_POSSIBLE_GAP : COLOUR_BACKUP_FAILED;
        return groupChainBreaks(
            breaks
                .filter(item => isPossibleGap(item, row) === possible)
                .map(item => Date.parse(item.chainbreakafter!)),
            (now - min) * BREAK_MERGE_FRACTION,
        ).map(({ time, count }) => ({
            xAxis: time,
            lineStyle: { color: colour },
            label: { color: colour, formatter: chainBreakLabel(possible, count) },
        }));
    };
    const chainBreaks = [...breakLines(false), ...breakLines(true)];

    const failed = {
        type: 'scatter',
        name: 'Failed',
        color: COLOUR_BACKUP_FAILED,
        symbol: 'diamond',
        symbolSize: 10,
        z: 4,
        data: items
            .filter(item => item.status === 'failed')
            .map(item => point(item, 0))
            .sort(byTime),
    };

    // Not in the legend, so hiding a series there does not hide these.
    const marks = {
        type: 'line',
        data: [],
        silent: true,
        markLine: chainBreaks.length ? {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', width: 2 },
            label: { position: 'insideEndTop' },
            data: chainBreaks,
        } : undefined,
        markArea: row.pitrfrom && row.pitrto ? {
            silent: true,
            itemStyle: { color: COLOUR_BACKUP_RESTORE_WINDOW },
            label: { formatter: 'Point-in-time restore', position: 'insideTop', fontSize: 11 },
            data: [[{ xAxis: Date.parse(row.pitrfrom) }, { xAxis: Date.parse(row.pitrto) }]],
        } : undefined,
    };

    return {
        animation: false,
        grid: { left: 8, right: 16, top: 24, bottom: 36, containLabel: true },
        legend: { show: true, bottom: 0, data: [...bars.map(bar => bar.name), failed.name] },
        xAxis: {
            type: 'time',
            min,
            max: now,
            splitLine: { show: false },
            axisLabel: {
                fontSize: 11,
                hideOverlap: true,
                formatter: (value: number) => formatAxisLabel(value, labelFormat, timezone),
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            axisLabel: { fontSize: 11, formatter: (value: number) => formatValue(value) },
        },
        tooltip: {
            ...TOOLTIP_BASE,
            trigger: 'item',
            position: positionTooltip,
            formatter: (params: unknown) => {
                const data = (params as { data?: { item?: BackupHistoryItem } }).data;
                return data?.item ? formatBackupTooltip(data.item) : '';
            },
        },
        series: [...bars, failed, marks, ...(args.plotLines?.length ? [buildPlotLineSeries(args.plotLines)] : [])],
    };
};
